import {Component, Input, OnInit} from "@angular/core";
import {LanguageChangeService} from "../../../services/language-change.service";
import {GdoStatus, IGDOObject} from "../../models/hav-models";
import {Config} from "../../../services/utils/gdo-config";

@Component({
  selector: "app-gdo-card",
  templateUrl: "./gdo-card.component.html",
  styleUrls: ["./gdo-card.component.css"]
})
export class GdoCardComponent implements OnInit {
  @Input() gdo: IGDOObject;
  @Input() viewType: string;
  @Input() member: boolean;
  homeName: string;
  isLoaderActive = false;
  gdoStatusImage = "../../../assets/images/gdo/GDOAlert.svg";
  deviceStatus: string;
  isIssue = false;
  isWarning = false;
  issueIcon = '';
  issueMessage = '';
  lastActivity: string;
  gdoStatusSet: Set<string> = new Set<string>(['01', '03', '07', '08', '09', '05']);
  isUnknownStatus = false;

  constructor(
    private langService: LanguageChangeService
  ) {}

  ngOnInit() {
    this.getGdoStatusImage(this.gdo.deviceStatus, this.gdo.percentage, this.gdo.deviceConnectionStatus);
    this.checkIssue(this.gdo);
    this.getLastActivity(this.gdo);
  }

  private getLastActivity(gdo: IGDOObject) {

    if (this.checkUpdateLastStatus(gdo) && this.gdoStatusSet.has(gdo.deviceStatus)) {
      this.convertDateFormat(gdo.lastUpdateStatus);
    } else {
        this.lastActivity = 'N/A';
    }
  }

  private convertDateFormat(timestamp: any) {

    timestamp = new Date(timestamp * 1000);
    timestamp = timestamp.toUTCString();
    timestamp = new Date(timestamp);

    let d = new Date(timestamp),
      month = '' + (d.getMonth()),
      day = '' + d.getDate(),
      year = d.getFullYear();

    if (day.length < 2) day = '0'+ day;

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    this.lastActivity = `${months[month]} ${day}, ${year}`;
  }

  private checkUpdateLastStatus(gdo: IGDOObject) {
    let currentDate = Math.floor(Date.now() / 1000);
    let lockStatus = currentDate - gdo.lastUpdateStatus;
    return gdo.deviceConnectionStatus === 1 || lockStatus <= Config.UPDATE_STATUS_SECONDS;
  }

  private getGdoStatusImage(deviceStatus: string, percentage: number, deviceConnectionStatus: number) {

    if (!deviceStatus || (this.checkUpdateLastStatus(this.gdo) && !this.gdoStatusSet.has(deviceStatus))) {
      this.setStatus(Config.GDO_STATUS.UNKNOWN_STATUS);
      this.isUnknownStatus = true;
      return;
    } else if (deviceConnectionStatus === 0) {
      this.setStatus(Config.GDO_STATUS.NO_CONNECTION);
      return;
    }

    switch (deviceStatus) {
      case Config.GDO_STATUS.CLOSED.code:
        this.setStatus(Config.GDO_STATUS.CLOSED);
        break;
      case Config.GDO_STATUS.OPEN.code:
        this.setStatus(Config.GDO_STATUS.OPEN);
        break;
      case Config.GDO_STATUS.CLOSING.code:
        this.setStatus(Config.GDO_STATUS.OPEN);
        break;
      case Config.GDO_STATUS.OPENING.code:
        this.setStatus(Config.GDO_STATUS.CLOSED);
        break;
      case Config.GDO_STATUS.PARTIAL.code:
      case Config.GDO_STATUS.INTERIM_POS.code:
        this.setPartialStatus(percentage, deviceStatus);
        break;
      default:
        this.gdoStatusImage = "../../../assets/images/gdo/GDOAlert.svg";
        this.deviceStatus = Config.GDO_STATUS.NO_CONNECTION[this.langService.getCurrentLanguage()];
    }
  }

  private setStatus(statusConfig: any) {
    this.gdoStatusImage = statusConfig.image;
    this.deviceStatus = statusConfig[this.langService.getCurrentLanguage()];
  }

  private setPartialStatus(percentage: number, deviceStatus: string) {
    const roundUp = Math.round(percentage / 10) * 10;
    const partialImages = {
      20: Config.GDO_STATUS.PARTIAL.image20,
      40: Config.GDO_STATUS.PARTIAL.image40,
      60: Config.GDO_STATUS.PARTIAL.image60,
      80: Config.GDO_STATUS.PARTIAL.image80,
      100: Config.GDO_STATUS.OPEN.image
    };
    this.gdoStatusImage = partialImages[roundUp] || Config.GDO_STATUS.PARTIAL.image20;
    if (deviceStatus === Config.GDO_STATUS.PARTIAL.code) {
      this.deviceStatus = (this.langService.getCurrentLanguage() === 'fr') ? `${Config.GDO_STATUS.PARTIAL[this.langService.getCurrentLanguage()]} ${percentage}%`
        : `${percentage}% ${Config.GDO_STATUS.PARTIAL[this.langService.getCurrentLanguage()]}`;
    } else {
      this.deviceStatus = (this.langService.getCurrentLanguage() === 'fr') ? `${Config.GDO_STATUS.PARTIAL[this.langService.getCurrentLanguage()]} ${percentage}%`
        : `${percentage}% ${Config.GDO_STATUS.PARTIAL[this.langService.getCurrentLanguage()]}`;
    }
  }

  private checkIssue(gdo: IGDOObject) {
    this.issueIcon = "../../../assets/images/gdo/alert-icon.svg";
    const checks = [
      this.checkConnectionIssue,
      this.openFailed,
      this.closeFailed,
      this.closeFailedSensorMisaligned,
      this.checkVacationMode,
      this.checkBattery,
      this.checkFirmwareUpdate
    ];

    for(const check of checks) {
      check.call(this, gdo);
      if (this.isIssue || this.isWarning) break;
    }
  }

  private checkBattery(gdo: IGDOObject) {
    const voltage = Number.parseInt(gdo.batteryVoltage);
    if (voltage <= 2200) {
      this.isIssue = true;
      this.issueIcon = "../../../assets/images/gdo/alert-icon.svg";
      this.issueMessage = Config.ERROR_MESSAGE.FLAT_BATTERY[this.langService.getCurrentLanguage()];
    } else if (voltage <= 2400) {
      this.isIssue = true;
      this.issueIcon = "../../../assets/images/gdo/alert-icon.svg";
      this.issueMessage = Config.ERROR_MESSAGE.LOW_BATTERY[this.langService.getCurrentLanguage()];
    }
  }

  private checkVacationMode(gdo: IGDOObject) {
    if (gdo.vacationMode && gdo.deviceStatus === GdoStatus.CLOSED) {
      this.isWarning = true;
      this.issueMessage = Config.ERROR_MESSAGE.VACATION_MODE[this.langService.getCurrentLanguage()];
      this.gdoStatusImage = "../../../assets/images/gdo/disable/Disable_Closed.svg";
      this.issueIcon = "../../../assets/images/gdo/Secure.svg";
    } else if (gdo.vacationMode && (gdo.deviceStatus === GdoStatus.OPEN || gdo.deviceStatus === GdoStatus.PARTIAL || gdo.deviceStatus === GdoStatus.INTERIM_POS)) {
      this.isWarning = true;
      this.issueMessage = Config.ERROR_MESSAGE.VACATION_MODE_PENDING[this.langService.getCurrentLanguage()];
      this.issueIcon = "../../../assets/images/gdo/Secure.svg";
    }
  }

  private checkFirmwareUpdate(gdo: IGDOObject) {
    if ((gdo.fwForceUpdate || gdo.lcuFwForceUpdate) &&
        gdo.firmwareUpdateStatus === 'NEW_FIRMWARE_AVAILABLE' && gdo.lcuFwStatus === 'NEW_FIRMWARE_AVAILABLE') {
        this.isWarning = true;
        this.issueMessage = Config.ERROR_MESSAGE.MULTIPLE_REQUIRED_FIRMWARE_UPDATE[this.langService.getCurrentLanguage()];
        this.issueIcon = "../../../assets/images/gdo/Reload.svg";
        this.setGdoImageSrc(gdo);

    } else if(gdo.firmwareUpdateStatus === 'NEW_FIRMWARE_AVAILABLE' && gdo.lcuFwStatus === 'NEW_FIRMWARE_AVAILABLE') {
      this.isWarning = true;
      this.issueMessage = Config.ERROR_MESSAGE.MULITPLE_AVAILABLE_FIRMWARE_UPDATE[this.langService.getCurrentLanguage()];
      this.issueIcon = "../../../assets/images/gdo/Reload.svg";

    } else if ((gdo.fwForceUpdate && gdo.firmwareUpdateStatus === 'NEW_FIRMWARE_AVAILABLE')
        || (gdo.lcuFwForceUpdate && gdo.lcuFwStatus === 'NEW_FIRMWARE_AVAILABLE')) {
      this.isWarning = true;
      this.issueMessage = Config.ERROR_MESSAGE.REQUIRED_FIRMWARE_UPDATE[this.langService.getCurrentLanguage()];
      this.issueIcon = "../../../assets/images/gdo/Reload.svg";
      this.setGdoImageSrc(gdo);

    } else if (gdo.firmwareUpdateStatus === 'NEW_FIRMWARE_AVAILABLE' || gdo.lcuFwStatus === 'NEW_FIRMWARE_AVAILABLE') {
      this.isWarning = true;
      this.issueMessage = Config.ERROR_MESSAGE.AVAILABLE_FIRMWARE_UPDATE[this.langService.getCurrentLanguage()];
      this.issueIcon = "../../../assets/images/gdo/Reload.svg";
    }
  }

  private checkConnectionIssue(gdo: IGDOObject) {
    if (gdo.deviceConnectionStatus === 0) {
      this.isWarning = true;
      this.isUnknownStatus = true;
      this.issueMessage = Config.ERROR_MESSAGE.NO_CONNECTION[this.langService.getCurrentLanguage()];
      this.gdoStatusImage = "../../../assets/images/gdo/GDOAlert.svg";
    }
  }

  private openFailed(gdo: IGDOObject) {
    if (gdo.closeAttemptCounter === '3' && gdo.alarmOD != '0' && gdo.alarmMI != '0') {
      this.isIssue = true;
      this.issueMessage = Config.ERROR_MESSAGE.OPEN_FAILED[this.langService.getCurrentLanguage()];
      this.setPartialGdoImageSrc(gdo.percentage);
    } else if (gdo.closeAttemptCounter === '3' && gdo.alarmOD != '0') {
      this.isIssue = true;
      this.issueMessage = Config.ERROR_MESSAGE.OPEN_FAILED[this.langService.getCurrentLanguage()];
      this.setFailurePartialGdoImageSrc(gdo.percentage);
    }
  }

  private closeFailed(gdo: IGDOObject) {
    if ((gdo.closeAttemptCounter === '1' || gdo.closeAttemptCounter === '2') && gdo.alarmOE != '0') {
      this.isIssue = true;
      this.issueMessage = Config.ERROR_MESSAGE.CLOSE_FAILED[this.langService.getCurrentLanguage()];
      this.setFailurePartialGdoImageSrc(gdo.percentage);
    } else if (gdo.closeAttemptCounter === '0' && gdo.alarmOE != '0' && gdo.alarmMI != '0') {
      this.isIssue = true;
      this.issueMessage = Config.ERROR_MESSAGE.CLOSE_FAILED[this.langService.getCurrentLanguage()];
      this.setPartialGdoImageSrc(gdo.percentage);
    }
  }

  private closeFailedSensorMisaligned(gdo: IGDOObject) {
    if (gdo.alarmMI == '0' && gdo.alignSensor == '3'
        || (gdo.alarmMI == '0' && gdo.alignSensor == '2' && (gdo.closeAttemptCounter == '2' || gdo.closeAttemptCounter == '1'))) {
      this.isIssue = true;
      this.issueMessage = Config.ERROR_MESSAGE.MISALIGNED_CLOSE_FAILED[this.langService.getCurrentLanguage()];
      this.setFailurePartialGdoImageSrc(gdo.percentage);
    } else if (gdo.closeAttemptCounter == '0' && gdo.alarmMI != '0')  {
      this.isIssue = true;
      this.issueMessage = Config.ERROR_MESSAGE.DISABLED_SENSOR_MISALIGNED[this.langService.getCurrentLanguage()];
      this.setPartialGdoImageSrc(gdo.percentage);
    }
  }

  private setGdoImageSrc(gdo: IGDOObject) {
    if (gdo.deviceStatus === GdoStatus.OPEN) {
      this.gdoStatusImage = "../../../assets/images/gdo/disable/Disable_Open 100.svg";
    } else if (gdo.deviceStatus === GdoStatus.CLOSED) {
      this.gdoStatusImage = "../../../assets/images/gdo/disable/Disable_Closed.svg";
    } else if (gdo.deviceStatus === GdoStatus.PARTIAL || gdo.deviceStatus === GdoStatus.INTERIM_POS) {
      this.setPartialGdoImageSrc(gdo.percentage);
    }
  }

  private setPartialGdoImageSrc(percentage: number) {

    if (percentage === 100) {
      this.gdoStatusImage = "../../../assets/images/gdo/disable/Disable_Open 100.svg";
    } else if (percentage === 0) {
      this.gdoStatusImage = "../../../assets/images/gdo/disable/Disable_Closed.svg";
    } else {
      const remainder = percentage % 10;
      if (remainder > 0 && remainder < 5) {
        percentage -= remainder;
      } else {
        percentage += (10 - remainder);
      }

      if (percentage > 0 && percentage < 20) {
        this.gdoStatusImage = "../../../assets/images/gdo/disable/Disable_Open 20.svg";
      } else if (percentage >= 20 && percentage <= 40) {
        this.gdoStatusImage = "../../../assets/images/gdo/disable/Disable_Open 40.svg";
      } else if (percentage > 40 && percentage <= 60) {
        this.gdoStatusImage = "../../../assets/images/gdo/disable/Disable_Open 60.svg";
      } else if (percentage > 60 && percentage <= 80) {
        this.gdoStatusImage = "../../../assets/images/gdo/disable/Disable_Open 80.svg";
      } else if (percentage > 80 && percentage <= 100) {
        this.gdoStatusImage = "../../../assets/images/gdo/disable/Disable_Open 100.svg";
      }
    }
  }

  private setFailurePartialGdoImageSrc(percentage: number) {
    const remainder = percentage % 10;
    if (remainder > 0 && remainder < 5) {
      percentage -= remainder;
    } else {
      percentage += (10 - remainder);
    }
    if (percentage > 0 && percentage < 20) {
      this.gdoStatusImage = "../../../assets/images/gdo/failure/Red_Open20.svg";
    } else if (percentage >= 20 && percentage <= 40) {
      this.gdoStatusImage = "../../../assets/images/gdo/failure/Red_Open40.svg";
    } else if (percentage > 40 && percentage <= 60) {
      this.gdoStatusImage = "../../../assets/images/gdo/failure/Red_Open60.svg";
    } else if (percentage > 60 && percentage <= 80) {
      this.gdoStatusImage = "../../../assets/images/gdo/failure/Red_Open80.svg";
    } else {
      this.gdoStatusImage = "../../../assets/images/gdo/failure/Red_Open100.svg";
    }
  }

}
